import React from 'react';
import { Node, Edge } from 'reactflow';
import { Box, Typography, CircularProgress } from '@mui/material';
import { ReactFlowProvider } from 'reactflow';
// Use dynamic import as a workaround for TypeScript module resolution issues
const PathologyGraph = React.lazy(() => import('./PathologyGraph'));

interface GraphData {
  nodes: {
    id: string;
    title: string;
    content: string;
    color: string;
  }[];
  edges: {
    source: string;
    target: string;
  }[];
}

interface PathologyGraphLoaderProps {
  jsonPath: string;
}

const PathologyGraphLoader: React.FC<PathologyGraphLoaderProps> = ({ jsonPath }) => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [nodes, setNodes] = React.useState<Node[]>([]);
  const [edges, setEdges] = React.useState<Edge[]>([]);

  React.useEffect(() => {
    const fetchGraphData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(jsonPath);
        if (!response.ok) {
          throw new Error(`Failed to load graph data: ${response.statusText}`);
        }
        
        const data: GraphData = await response.json();
        
        // Transform the nodes to React Flow format
        const flowNodes: Node[] = data.nodes.map((node, index) => ({
          id: node.id,
          type: 'custom',
          position: { x: index * 250, y: 0 }, // Initial positions, will be auto-arranged
          data: {
            title: node.title,
            content: node.content,
            color: node.color
          }
        }));
        
        // Transform the edges to React Flow format
        const flowEdges: Edge[] = data.edges.map((edge, index) => ({
          id: `e-${index}`,
          source: edge.source,
          target: edge.target,
          animated: false,
          style: { stroke: '#A55A7E', strokeWidth: 2 },
          type: 'smoothstep'
        }));
        
        setNodes(flowNodes);
        setEdges(flowEdges);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error loading graph data');
        console.error('Error loading graph data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGraphData();
  }, [jsonPath]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ my: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
        <Typography color="error">Error loading graph: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ my: 2, height: 450, width: '100%', border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <ReactFlowProvider>
        <React.Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress color="secondary" />
          </Box>
        }>
          <PathologyGraph initialNodes={nodes} initialEdges={edges} />
        </React.Suspense>
      </ReactFlowProvider>
    </Box>
  );
};

export default PathologyGraphLoader;
